/**
 * HERO product-mock module: "Dokumente" (DMS document grid + detail view).
 */
"use client";

import {
  ArrowUpDown,
  FileSpreadsheet,
  FileText,
  Folder,
  Image as ImageIcon,
  LayoutGrid,
  List,
  ListFilter,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useTranslations } from "next-intl";
import { H, MODULE_HEIGHT } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";
import { HeroClickPulseDot } from "./hero-click-pulse-dot";
import {
  type DocId,
  type DocItem,
  documents,
  getDocById,
} from "./module-dokumente-data";
import { DokumenteDetail } from "./module-dokumente-detail";
import { DokumenteImportView } from "./module-dokumente-import";
import { DokumentePostkorbView } from "./module-dokumente-postkorb";
import {
  DokumenteSidebar,
  type DokumenteSidebarSelection,
} from "./module-dokumente-sidebar";
import {
  ThumbImage,
  ThumbTable,
  ThumbText,
} from "./module-dokumente-thumbnails";

const headerAction: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 11px",
  borderRadius: 8,
  cursor: "default",
};

function DocCard(props: {
  readonly doc: DocItem;
  readonly onOpen: (id: DocId) => void;
  readonly showClickPulse?: boolean;
}): ReactNode {
  const t = useTranslations("marketing.moduleDokumente");
  const { doc } = props;
  const Icon =
    doc.icon === "file-spreadsheet"
      ? FileSpreadsheet
      : doc.icon === "image"
        ? ImageIcon
        : FileText;
  return (
    <button
      type="button"
      className="mkhero-doc-card"
      aria-label={t("dokumentOeffnen", { name: doc.name })}
      onClick={() => {
        props.onOpen(doc.id);
      }}
      style={{
        position: "relative",
        border: "1px solid #E9EBEF",
        borderRadius: 11,
        overflow: "hidden",
        background: "#fff",
        cursor: "pointer",
        padding: 0,
        textAlign: "left",
        width: "100%",
        fontFamily: H.sans,
      }}
    >
      <div
        style={{
          position: "relative",
          height: 146,
          background: "#F0F1F4",
          overflow: "hidden",
        }}
      >
        {doc.kind === "text" ? <ThumbText /> : null}
        {doc.kind === "table" ? <ThumbTable /> : null}
        {doc.kind === "image" ? <ThumbImage /> : null}
        <span
          style={{
            position: "absolute",
            top: 9,
            right: 11,
            fontFamily: H.label,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.03em",
            color: doc.badgeColor,
          }}
        >
          {doc.badge}
        </span>
      </div>
      <div
        style={{
          padding: "9px 10px",
          display: "flex",
          alignItems: "center",
          gap: 7,
          borderTop: "1px solid #F1F2F4",
        }}
      >
        <span
          style={{
            color: doc.iconColor,
            display: "inline-flex",
            flexShrink: 0,
          }}
        >
          <Icon width={14} height={14} strokeWidth={1.8} />
        </span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: H.ink,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {doc.name}
        </span>
      </div>
      {props.showClickPulse === true ? (
        <HeroClickPulseDot placement="corner-tr" />
      ) : null}
    </button>
  );
}

function DokumenteGrid(props: {
  readonly onOpenDoc: (id: DocId) => void;
  readonly showDocPulseOnFirst: boolean;
}): ReactNode {
  const t = useTranslations("marketing.moduleDokumente");
  const tData = useTranslations("marketing.moduleDokumenteData");
  return (
    <section
      style={{
        flex: 1,
        minWidth: 0,
        padding: "20px 22px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 9,
            fontSize: 18,
            fontWeight: 700,
            color: H.ink,
          }}
        >
          <span
            style={{ color: "rgba(11,15,25,0.55)", display: "inline-flex" }}
          >
            <Folder width={18} height={18} strokeWidth={1.9} />
          </span>
          {t("alleDokumente")}
        </span>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 13,
            color: H.inkMute,
          }}
        >
          <div
            style={{
              display: "flex",
              border: "1px solid #E7E9ED",
              borderRadius: 9,
              overflow: "hidden",
            }}
          >
            <span
              style={{
                padding: "7px 9px",
                background: H.accentSoft,
                color: H.accent,
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <LayoutGrid width={15} height={15} strokeWidth={1.9} />
            </span>
            <span
              style={{
                padding: "7px 9px",
                color: "rgba(11,15,25,0.4)",
                display: "inline-flex",
                alignItems: "center",
                borderLeft: "1px solid #E7E9ED",
              }}
            >
              <List width={15} height={15} strokeWidth={1.9} />
            </span>
          </div>
          <span style={headerAction}>
            <ListFilter width={14} height={14} strokeWidth={1.8} />
            {t("filter")}
          </span>
          <span style={headerAction}>
            <ArrowUpDown width={14} height={14} strokeWidth={1.8} />
            {t("sortierung")}
          </span>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4,1fr)",
          gap: 14,
        }}
      >
        {documents(tData).map((doc, index) => (
          <DocCard
            key={doc.id}
            doc={doc}
            showClickPulse={props.showDocPulseOnFirst && index === 0}
            onOpen={props.onOpenDoc}
          />
        ))}
      </div>
    </section>
  );
}

export function ModuleDokumente(props: ModuleProps): ReactNode {
  const [sidebarSelection, setSidebarSelection] =
    useState<DokumenteSidebarSelection>("alle");
  const tData = useTranslations("marketing.moduleDokumenteData");
  const [selectedId, setSelectedId] = useState<DocId | null>(null);
  const [showClickPulse, setShowClickPulse] = useState<boolean>(true);
  const sidebarDrivenRef = useRef<boolean>(false);
  const selectedDoc: DocItem | null =
    selectedId !== null ? getDocById(tData, selectedId) ?? null : null;

  const dismissClickPulse = useCallback((): void => {
    setShowClickPulse(false);
  }, []);

  useEffect(() => {
    // Sidebar already picked the view; the chip sync must not reset it.
    if (sidebarDrivenRef.current) {
      sidebarDrivenRef.current = false;
      return;
    }
    if (props.active === "posteingangsscan") {
      setSelectedId(null);
      setSidebarSelection("postkorb");
      setShowClickPulse(false);
      return;
    }
    if (props.active === "dokumente") {
      setSelectedId(null);
      setSidebarSelection("alle");
    }
  }, [props.active]);

  const showGridPulses: boolean =
    showClickPulse && selectedDoc === null && sidebarSelection === "alle";

  return (
    <div
      className="mkhero"
      style={{ display: "flex", height: MODULE_HEIGHT, fontFamily: H.sans }}
    >
      {selectedDoc === null ? (
        <>
          <DokumenteSidebar
            selection={sidebarSelection}
            showPostkorbPulse={showGridPulses}
            onSelectionChange={(selection: DokumenteSidebarSelection): void => {
              if (selection === "postkorb") {
                dismissClickPulse();
              }
              const nextActive = selection === "postkorb" ? "posteingangsscan" : "dokumente";
              sidebarDrivenRef.current = nextActive !== props.active;
              props.setActive(nextActive);
              setSidebarSelection(selection);
            }}
          />
          {sidebarSelection === "postkorb" ? (
            <DokumentePostkorbView />
          ) : sidebarSelection === "import" ? (
            <DokumenteImportView />
          ) : (
            <DokumenteGrid
              showDocPulseOnFirst={showGridPulses}
              onOpenDoc={(id: DocId): void => {
                dismissClickPulse();
                setSelectedId(id);
              }}
            />
          )}
        </>
      ) : (
        <DokumenteDetail
          doc={selectedDoc}
          onBack={() => {
            setSelectedId(null);
          }}
        />
      )}
    </div>
  );
}
